import React, { useState } from 'react'
import {
  Box,
  Container,
  Divider,
  makeStyles,
  Tab,
  Tabs,
  Typography,
} from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { useReactiveVar } from '@apollo/client'
import { BuildingFragment } from '../../generated/graphql'
import { BuildingBasicInfoTab } from './basic-info-tab/BuildingBasicInfoTab'
import { BuildingEnergySystemTab } from './energy-system-tab/BuildingEnergySystemTab'
import { BuildingReportTab } from './report-tab/BuildingReportTab'
import { activeVersionVar, calculatorVar } from '../../apollo/reactive-vars'
import { IndicatorIcons } from './indicator-icons/IndicatorIcons'
import { BuildingOptions } from './BuildingOptions'
import { BuildingBoreholeFieldTab } from './borehole-field-tab/BuildingBoreholeFieldTab'
import { isConfirmationValid } from '../../borehole-field-container/borehole-field-confirmation-validator'
import { LabelWithAlertBadge } from '../common/LabelWithAlertBadge'
import {
  createConfirmationData,
  createHarvestedDataForSystemContainer,
  createWasteInputDataForSystemContainer,
} from '../../borehole-field-container/borehole-field-data-creator'

const useStyles = makeStyles((theme) => ({
  title: {
    maxWidth: 300,
    overflow: 'hidden',
    textOverflow: 'ellipsis',

    [theme.breakpoints.up('lg')]: {
      maxWidth: 600,
    },
  },
}))

type Props = {
  building: BuildingFragment
  onClose: () => void
}

export function BuildingDrawerContent({
  building,
  onClose,
}: Props): React.ReactElement {
  const classes = useStyles()
  const activeVersion = useReactiveVar(activeVersionVar)
  const [tab, setTab] = useState(0)
  const { t } = useTranslation()
  const calculator = useReactiveVar(calculatorVar)

  if (!activeVersion) {
    return <div />
  }

  const buildingCalculator = calculator?.findBuilding(building.id)

  const providers = buildingCalculator?.getProviders() ?? []

  const harvested = createHarvestedDataForSystemContainer(providers)
  const wasteInput = createWasteInputDataForSystemContainer(providers)
  const currentBoreholeData = createConfirmationData(
    building,
    harvested,
    wasteInput,
  )

  return (
    <Box>
      <Container>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mt={4}
          mb={2}
        >
          <Typography variant="h3" className={classes.title}>
            {building.name}
          </Typography>

          <BuildingOptions building={building} onClose={onClose} />
        </Box>

        <Box mb={2}>
          <IndicatorIcons building={building} />
        </Box>

        <Tabs value={tab} onChange={(_, index: number) => setTab(index)}>
          <Tab id="basic-info-tab" label={t('building.tabs.basicInfo.title')} />
          <Tab
            id="energy-system-tab"
            label={t('building.tabs.energySystem.title')}
          />
          <Tab id="report-tab" label={t('report.title')} />
          <Tab
            id="borehole-field-tab"
            label={
              <LabelWithAlertBadge
                label={t('boreholeField.title')}
                showBadge={!isConfirmationValid(building, currentBoreholeData)}
              />
            }
          />
        </Tabs>
      </Container>

      <Divider />

      {tab === 0 && <BuildingBasicInfoTab building={building} />}
      {tab === 1 && <BuildingEnergySystemTab building={building} />}
      {tab === 2 && <BuildingReportTab building={building} />}
      {tab === 3 && <BuildingBoreholeFieldTab building={building} />}
    </Box>
  )
}
